import Vue from 'vue';
import { timeDay, timeMillisecond } from 'd3-time';
import { timeFormat } from 'd3-time-format';
import { format } from 'd3';

// returns from the start of the first day to the end of the last
export const getDateEndpoints = ([startDate, endDate]: [Date, Date]): [Date, Date] => {
  const startFloor = timeDay.floor(startDate);
  const endCeil = timeMillisecond.offset(timeDay.ceil(endDate), -1);
  return [startFloor, endCeil];
};

export const dateFormat = timeFormat('%m/%d/%y');
export const dateTimeLabelFormat = timeFormat('%b %_d');
export const dateMonthYearFormat = timeFormat('%b %_d, %Y');
export const dateExportFormat = timeFormat('%Y-%m-%d');
export const dateTimeExportFormat = timeFormat('%Y-%m-%d at %H:%M%p');
export const monthYearLabel = timeFormat('%B %Y');
export const shortMonthYearLabel = timeFormat('%b %y');
export const dollarFormat = format('$.2f');
export const dateTimeLongFormat = timeFormat('%d-%m-%Y at %H:%M%p');
export const dateTimeShortFormat = timeFormat('%d %m %Y %H:%M%p');

export const dateTimeLocalFormat = (date: Date | string): string => {
  // Convert input date to a Date object if it's a string
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  // Format the date as "2 Feb 01:47am" using toLocaleString with options
  return dateObj
    .toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    })
    .replace(/ /g, ' ');
};

const msPerMinute = 60 * 1000;
const msPerHour = msPerMinute * 60;
const msPerDay = msPerHour * 24;
const msPerMonth = msPerDay * 30;
const msPerYear = msPerDay * 365;
export function shortTimeAgo(d: Date) {
  const now = new Date();
  // @ts-ignore
  const elapsed = now - d;
  if (elapsed < msPerMinute) {
    return `${Math.floor(elapsed / 1000)}s`;
  }
  if (elapsed < msPerHour) {
    return `${Math.floor(elapsed / msPerMinute)}m`;
  }
  if (elapsed < msPerDay) {
    return `${Math.floor(elapsed / msPerHour)}h`;
  }
  if (elapsed < msPerMonth) {
    return `${Math.floor(elapsed / msPerDay)}d`;
  }
  if (elapsed < msPerYear) {
    return `${Math.floor(elapsed / msPerMonth)}m`;
  }
  return `${Math.floor(elapsed / msPerYear)}y`;
}

export function shortTimeAgoExtended(dt: Date) {
  const now = new Date();
  const d = dt instanceof Date && isFinite(dt.getTime()) ? dt : new Date(dt);
  // @ts-ignore
  let elapsed = now - d;
  if (elapsed < 0) {
    elapsed = -elapsed;
  }
  let amount;
  if (elapsed < msPerMinute) {
    amount = Math.floor(elapsed / msPerMinute);
    return `${amount} ${amount === 1 ? 'second' : 'seconds'}`;
  }
  if (elapsed < msPerHour) {
    amount = Math.floor(elapsed / msPerMinute);
    return `${amount} ${amount === 1 ? 'minute' : 'minutes'}`;
  }
  if (elapsed < msPerDay) {
    amount = Math.floor(elapsed / msPerHour);
    return `${amount} ${amount === 1 ? 'hour' : 'hours'}`;
  }
  if (elapsed < msPerMonth) {
    amount = Math.floor(elapsed / msPerDay);
    return `${amount} ${amount === 1 ? 'day' : 'days'}`;
  }
  if (elapsed < msPerYear) {
    amount = Math.floor(elapsed / msPerMonth);
    return `${amount} ${amount === 1 ? 'month' : 'months'}`;
  }
  amount = Math.floor(elapsed / msPerYear);
  return `${amount} ${amount === 1 ? 'year' : 'years'}`;
}

export function daysAgo(d: Date) {
  const now = new Date();
  // @ts-ignore
  const elapsed = now - d;
  return Math.floor(elapsed / msPerDay);
}

function formatTime(date: Date) {
  // like 8:30am or 11:45pm
  return timeFormat('%-I:%M %p')(date).toLowerCase();
}

function formatDate(date: Date) {
  const day = timeFormat('%e %B')(date);
  const time = formatTime(date);
  return `${day} at ${time}`;
}

function getDayOfWeek(date: Date) {
  return timeFormat('%A')(date);
}

export function quickDateTime(date: Date) {
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diff <= 1) {
    const sameDay = date.toDateString() === now.toDateString();
    const yesterday = date.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();

    if (sameDay) {
      return `Today at ${formatTime(date)}`;
    }
    if (yesterday) {
      return `Yesterday at ${formatTime(date)}`;
    }
  }

  if (diff <= 3) {
    return `${getDayOfWeek(date)} at ${formatTime(date)}`;
  }

  return formatDate(date);
}

Vue.filter('shortTimeAgo', shortTimeAgo);
Vue.filter('shortTimeAgoExtended', shortTimeAgoExtended);
Vue.filter('quickDateTime', quickDateTime);
Vue.filter('dateFormat', dateFormat);
Vue.filter('dateTimeLabelFormat', dateTimeLabelFormat);
Vue.filter('dateTimeLongFormat', dateTimeLongFormat);
Vue.filter('dateTimeLocalFormat', dateTimeLocalFormat);
Vue.filter('dateMonthYearFormat', dateMonthYearFormat);
Vue.filter('dateExportFormat', dateExportFormat);
Vue.filter('dateTimeExportFormat', dateTimeExportFormat);
Vue.filter('monthYearLabel', monthYearLabel);
Vue.filter('shortMonthYearLabel', shortMonthYearLabel);
Vue.filter('daysAgo', daysAgo);
Vue.filter('dollars', dollarFormat);
